// ============================================================================
// THE LAST DEGEN - BATTLE ROYALE CRASH GAME
// ============================================================================
// Replace boring "1.05x... 1.10x..." with actual gameplay
// Multiple climbers on a mountain, attacks, real PvP
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';

// Dark night mountain color palette
const COLORS = {
  bg: '#0a0f1a',           // Deep navy background
  mountain: '#1a2233',     // Dark mountain
  mountainLight: '#2a3344', // Mountain highlights
  snow: '#e8eef5',         // Snow caps
  text: '#8899aa',         // Muted text
  textBright: '#c8d4e0',   // Brighter text
  gold: '#c9a227',         // Gold accents
  bronze: '#cd7f32',       // Bronze tier
  silver: '#c0c0c0',       // Silver tier
  goldTier: '#ffd700',     // Gold tier
  danger: '#ff4444',       // Fall/danger
  safe: '#44cc66',         // Safe/cash out
  chat: '#334455',         // Chat bg
  cyan: '#4ecdc4',         // Climbing accent
};

// Tier config (matches server)
const TIERS = {
  bronze: { fee: 1, attacks: 1, color: COLORS.bronze, emoji: '🥉' },
  silver: { fee: 2, attacks: 2, color: COLORS.silver, emoji: '🥈' },
  gold: { fee: 3, attacks: 3, color: COLORS.goldTier, emoji: '🥇' },
};

// 15 unique climber characters - CSS pixel art style
const CLIMBERS = {
  bob: { name: 'BOB', head: '#ffcc99', body: '#3366cc', hair: '#8b4513', eyes: '#000' },
  chad: { name: 'CHAD', head: '#ffe0bd', body: '#ff0000', hair: '#ffd700', eyes: '#00f' },
  pepe: { name: 'PEPE', head: '#7cba5f', body: '#5a9a3d', hair: null, eyes: '#000' },
  wojak: { name: 'WOJAK', head: '#f5deb3', body: '#808080', hair: '#8b4513', eyes: '#000' },
  doge: { name: 'DOGE', head: '#f4a460', body: '#daa520', hair: '#f4a460', eyes: '#000' },
  ape: { name: 'APE', head: '#8b4513', body: '#654321', hair: '#5c4033', eyes: '#ff0' },
  punk: { name: 'PUNK', head: '#ffcc99', body: '#000', hair: '#ff00ff', eyes: '#0ff' },
  alien: { name: 'ALIEN', head: '#90ee90', body: '#006400', hair: null, eyes: '#ff0' },
  robot: { name: 'ROBOT', head: '#c0c0c0', body: '#404040', hair: null, eyes: '#f00' },
  ninja: { name: 'NINJA', head: '#1a1a1a', body: '#000', hair: null, eyes: '#fff' },
  pirate: { name: 'PIRATE', head: '#ffcc99', body: '#8b0000', hair: '#000', eyes: '#000' },
  zombie: { name: 'ZOMBIE', head: '#98fb98', body: '#556b2f', hair: '#2f4f2f', eyes: '#f00' },
  ghost: { name: 'GHOST', head: '#f0f0f0', body: '#e0e0e0', hair: null, eyes: '#000' },
  devil: { name: 'DEVIL', head: '#ff4444', body: '#8b0000', hair: '#ff0', eyes: '#ff0' },
  angel: { name: 'ANGEL', head: '#fff5ee', body: '#fff', hair: '#ffd700', eyes: '#87ceeb' },
};

// Pixel art climber component
const ClimberSprite = ({ character, size = 22, isMe, isSafe }) => {
  const c = CLIMBERS[character] || CLIMBERS.bob;
  const s = size;
  const headSize = s * 0.7;
  const bodyWidth = s;
  const bodyHeight = s * 0.8;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      opacity: isSafe ? 0.6 : 1,
    }}>
      {/* Hair/hat */}
      {c.hair && (
        <div style={{
          width: headSize * 1.1,
          height: headSize * 0.3,
          backgroundColor: c.hair,
          borderRadius: '4px 4px 0 0',
          marginBottom: '-2px',
        }} />
      )}
      {/* Head */}
      <div style={{
        width: headSize,
        height: headSize,
        backgroundColor: c.head,
        borderRadius: '4px',
        position: 'relative',
        border: isMe ? '2px solid #fff' : '1px solid rgba(255,255,255,0.3)',
        boxShadow: isMe ? '0 0 10px rgba(255,255,255,0.5)' : 'none',
      }}>
        {/* Eyes */}
        <div style={{
          position: 'absolute',
          top: '35%',
          left: '20%',
          width: '20%',
          height: '20%',
          backgroundColor: c.eyes,
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          top: '35%',
          right: '20%',
          width: '20%',
          height: '20%',
          backgroundColor: c.eyes,
          borderRadius: '50%',
        }} />
      </div>
      {/* Body */}
      <div style={{
        width: bodyWidth,
        height: bodyHeight,
        backgroundColor: c.body,
        borderRadius: '0 0 4px 4px',
        marginTop: '-2px',
      }} />
    </div>
  );
};

// Generate simulated ghost climbers for demo/spectating - runs continuously when few players
const useGhostClimbers = (realClimberCount, phase) => {
  const [ghosts, setGhosts] = useState([]);
  const [simLevel, setSimLevel] = useState(1);
  const [simPhase, setSimPhase] = useState('climbing');
  const ghostsRef = useRef([]);
  const simStartTimeRef = useRef(Date.now());
  const crashPointRef = useRef(2 + Math.random() * 3); // Random crash between 2x and 5x

  useEffect(() => {
    // Only simulate when NO real climbers (demo mode)
    if (realClimberCount >= 1) {
      setGhosts([]);
      ghostsRef.current = [];
      return;
    }

    // Initialize ghosts if needed
    const initGhosts = () => {
      const ghostChars = ['pepe', 'wojak', 'doge', 'chad', 'alien', 'robot', 'ninja'];
      const numGhosts = Math.min(6 - realClimberCount, 5);
      ghostsRef.current = Array.from({ length: numGhosts }, (_, i) => ({
        id: `ghost_${i}`,
        character: ghostChars[i % ghostChars.length],
        tier: ['bronze', 'silver', 'gold'][Math.floor(Math.random() * 3)],
        level: 1,
        cashedOut: false,
        cashoutTarget: 1.3 + Math.random() * 2.5, // Cash out between 1.3x and 3.8x
        isGhost: true,
      }));
      setSimLevel(1);
      setSimPhase('climbing');
      simStartTimeRef.current = Date.now();
      crashPointRef.current = 2 + Math.random() * 3; // New random crash point
    };

    if (ghostsRef.current.length === 0) {
      initGhosts();
    }

    // Run simulation tick every 150ms
    const interval = setInterval(() => {
      setSimPhase(currentPhase => {
        if (currentPhase !== 'climbing') return currentPhase;

        // Advance simulation level (climb over ~12 seconds)
        const elapsed = Date.now() - simStartTimeRef.current;
        const targetLevel = 1 + (elapsed / 12000) * 4; // Reach 5x in 12 seconds
        const newLevel = Math.min(targetLevel, 5);
        setSimLevel(newLevel);

        // Update ghost positions
        const updated = ghostsRef.current.map(ghost => {
          if (ghost.cashedOut) return ghost;

          // Ghost follows level with variation
          const ghostLevel = Math.min(newLevel * (0.85 + Math.random() * 0.3), 5);

          // Check if ghost should cash out
          if (ghostLevel >= ghost.cashoutTarget && !ghost.cashedOut) {
            return { ...ghost, level: ghost.cashoutTarget, cashedOut: true, cashoutLevel: ghost.cashoutTarget };
          }

          return { ...ghost, level: ghostLevel };
        });

        ghostsRef.current = updated;
        setGhosts([...updated]);

        // Check for crash
        if (newLevel >= crashPointRef.current || newLevel >= 5) {
          // Mark anyone not cashed out as fell
          ghostsRef.current = ghostsRef.current.map(g =>
            g.cashedOut ? g : { ...g, fell: true }
          );
          setGhosts([...ghostsRef.current]);

          // Reset after 3 seconds
          setTimeout(() => {
            initGhosts();
          }, 3000);

          return 'crashed';
        }

        return 'climbing';
      });
    }, 150);

    return () => clearInterval(interval);
  }, [realClimberCount]);

  return { ghosts, simLevel, simPhase };
};

// Mountain component - draws the mountain with climbers
const Mountain = ({ climbers, currentLevel, maxLevel = 5, myHandle, phase }) => {
  const climberArray = Object.entries(climbers || {});
  const realClimberCount = climberArray.length;

  // Get ghost climbers for demo when few real players (runs its own simulation loop)
  const { ghosts, simLevel, simPhase } = useGhostClimbers(realClimberCount, phase);

  // Use real game level when playing, simulation level for demo
  const displayLevel = realClimberCount > 0 ? currentLevel : simLevel;
  const displayPhase = realClimberCount > 0 ? phase : simPhase;

  // Combine real climbers with ghosts
  const allClimbers = [
    ...climberArray,
    ...ghosts.map(g => [g.id, g])
  ];

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '400px',
      background: `linear-gradient(180deg, ${COLORS.bg} 0%, #0d1520 100%)`,
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${COLORS.mountain}`,
    }}>
      {/* Stars */}
      {[...Array(30)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: `${(i * 37) % 100}%`,
          top: `${(i * 13) % 40}%`,
          width: '2px',
          height: '2px',
          backgroundColor: '#ffffff',
          opacity: 0.3 + (i % 3) * 0.2,
          borderRadius: '50%',
        }} />
      ))}

      {/* Mountain shape */}
      <svg
        viewBox="0 0 400 300"
        style={{ position: 'absolute', bottom: 0, width: '100%', height: '80%' }}
        preserveAspectRatio="none"
      >
        {/* Main mountain */}
        <polygon
          points="200,20 50,280 350,280"
          fill={COLORS.mountain}
          stroke={COLORS.mountainLight}
          strokeWidth="1"
        />
        {/* Snow cap */}
        <polygon
          points="200,20 170,70 230,70"
          fill={COLORS.snow}
          opacity="0.9"
        />
        {/* Level markers */}
        {[1, 2, 3, 4, 5].map(level => {
          const y = 280 - (level * 50);
          return (
            <g key={level}>
              <line x1="55" y1={y} x2="345" y2={y} stroke={COLORS.mountainLight} strokeWidth="1" strokeDasharray="3,3" opacity="0.3" />
              <text x="370" y={y + 4} fill={COLORS.text} fontSize="12" fontFamily="monospace">
                {level}x
              </text>
            </g>
          );
        })}
      </svg>

      {/* Climbers (real + ghost) */}
      {allClimbers.map(([handle, climber], index) => {
        // Position based on their level (1x = bottom, 5x = top)
        const levelPct = ((climber.level || 1) - 1) / (maxLevel - 1); // 0 to 1
        const yPos = 85 - (levelPct * 65); // 85% at bottom, 20% at top
        const xOffset = (index - allClimbers.length / 2) * 35; // Spread horizontally
        const myHandleNoPrefix = myHandle?.replace(/^\$/, '');
        const isMe = handle === myHandle || handle === myHandleNoPrefix;
        const isSafe = climber.cashedOut;
        const isSlipping = climber.slipped > 0;
        const isGhost = climber.isGhost;

        return (
          <div
            key={handle}
            style={{
              position: 'absolute',
              left: `calc(50% + ${xOffset}px)`,
              top: `${yPos}%`,
              transform: 'translate(-50%, -50%)',
              transition: 'top 0.3s ease-out',
              textAlign: 'center',
              zIndex: isMe ? 10 : isGhost ? 0 : 1,
              animation: isSlipping && !isSafe ? 'shake 0.3s ease-in-out' : 'none',
              opacity: isGhost ? 0.4 : 1, // Ghosts are semi-transparent
            }}
          >
            {/* Climber sprite */}
            <ClimberSprite
              character={climber.character || 'bob'}
              size={22}
              isMe={isMe}
              isSafe={isSafe}
            />
            {/* Safe flag */}
            {isSafe && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '14px',
              }}>{isGhost ? '👻' : '⛳'}</div>
            )}
            {/* Name tag */}
            <div style={{
              fontSize: '9px',
              color: isGhost ? '#666' : (isSafe ? COLORS.safe : COLORS.textBright),
              marginTop: '2px',
              whiteSpace: 'nowrap',
              textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              fontWeight: isMe ? 'bold' : 'normal',
              fontStyle: isGhost ? 'italic' : 'normal',
            }}>
              {isGhost ? '(sim)' : handle}
            </div>
            {/* Tier badge */}
            <div style={{ fontSize: '8px', opacity: isGhost ? 0.5 : 0.8 }}>
              {TIERS[climber.tier]?.emoji || '🥉'}
            </div>
          </div>
        );
      })}

      {/* Current level display - show sim level when in demo mode */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '36px',
        fontWeight: 'bold',
        fontFamily: 'monospace',
        color: displayPhase === 'crashed' ? COLORS.danger : COLORS.cyan,
        textShadow: `0 2px 10px ${displayPhase === 'crashed' ? COLORS.danger : COLORS.cyan}40`,
        opacity: ghosts.length > 0 && realClimberCount === 0 ? 0.6 : 1,
      }}>
        {(displayLevel || 1).toFixed(2)}x
      </div>

      {/* Phase indicator - show real phase with demo overlay */}
      <div style={{
        position: 'absolute',
        top: '60px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '12px',
        fontWeight: 'bold',
        color: phase === 'betting' ? COLORS.gold : phase === 'climbing' ? COLORS.safe : COLORS.danger,
        textTransform: 'uppercase',
        letterSpacing: '2px',
      }}>
        {phase === 'betting' ? 'JOIN NOW' : phase === 'climbing' ? 'CLIMBING!' : 'AVALANCHE!'}
      </div>
      {/* Demo phase overlay when simulating */}
      {ghosts.length > 0 && realClimberCount === 0 && (
        <div style={{
          position: 'absolute',
          top: '78px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '10px',
          color: displayPhase === 'crashed' ? COLORS.danger : COLORS.safe,
          opacity: 0.7,
        }}>
          {displayPhase === 'crashed' ? '(demo: avalanche!)' : '(demo: climbing)'}
        </div>
      )}

      {/* Sim mode indicator */}
      {ghosts.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '9px',
          color: '#555',
          fontStyle: 'italic',
        }}>
          Demo mode: simulated climbers shown
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
          25% { transform: translate(-50%, -50%) rotate(-5deg); }
          75% { transform: translate(-50%, -50%) rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

// Main MountainClimber component
const MountainClimber = ({
  isOpen,
  onClose,
  gameWallet,
  setGameWallet,
  sessionId,
  API_URL,
  handcashUser,
  triggerRakeNotification,
  Sounds,
  wsRef,
  bsvPrice = 18.5, // BSV price for fee calculation
}) => {
  // Game state from server
  const [gameState, setGameState] = useState(null);
  const [selectedTier, setSelectedTier] = useState('bronze');
  const [selectedCharacter, setSelectedCharacter] = useState('bob');
  const [betAmount, setBetAmount] = useState(10000);
  const [myClimber, setMyClimber] = useState(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  // UI state
  const [isJoining, setIsJoining] = useState(false);
  const [isCashingOut, setIsCashingOut] = useState(false);
  const [isAttacking, setIsAttacking] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const chatRef = useRef(null);
  const lastRoundId = useRef(null);
  const lastPhase = useRef(null);
  const lastLevel = useRef(1);
  const lastSlipCount = useRef(0);
  const wasInRound = useRef(false);
  const lastBettingSeconds = useRef(null);

  // Fetch game state
  const fetchGameState = async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/mountain/state`);
      const data = await res.json();
      setGameState(data);

      // Update chat
      if (data.round?.chat) {
        setChatMessages(data.round.chat);
      }

      // Reset on new round FIRST (before setting myClimber)
      if (data.round?.id !== lastRoundId.current) {
        lastRoundId.current = data.round?.id;
        setError(null);
        // Don't reset myClimber here - let the check below handle it
      }

      // Check if I'm in this round
      if (data.round && handcashUser?.handle) {
        const myHandle = handcashUser.handle;
        const handleNoPrefix = myHandle.replace(/^\$/, '');
        const me = data.round.climbers?.[myHandle] || data.round.climbers?.[handleNoPrefix];
        setMyClimber(me || null);
      } else {
        setMyClimber(null);
      }
    } catch (e) {
      console.error('Mountain state fetch error:', e);
    }
  };

  // WebSocket message handler
  const handleWsMessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'mountain_state' && data.state) {
        setGameState(data.state);

        // Update chat
        if (data.state.round?.chat) {
          setChatMessages(data.state.round.chat);
        }

        // Reset on new round FIRST (before setting myClimber)
        if (data.state.round?.id !== lastRoundId.current) {
          lastRoundId.current = data.state.round?.id;
          setError(null);
          // Don't reset myClimber here - let the check below handle it
        }

        // Check if I'm in this round
        if (data.state.round && handcashUser?.handle) {
          const myHandle = handcashUser.handle;
          const handleNoPrefix = myHandle.replace(/^\$/, '');
          const me = data.state.round.climbers?.[myHandle] || data.state.round.climbers?.[handleNoPrefix];
          setMyClimber(me || null);
        } else {
          setMyClimber(null);
        }
      }
    } catch (e) {
      // Not a mountain message, ignore
    }
  };

  // Subscribe to Mountain room via WebSocket or fallback to polling
  useEffect(() => {
    if (!isOpen) return;

    const ws = wsRef?.current;
    const useWs = ws && ws.readyState === WebSocket.OPEN;

    if (useWs) {
      // Subscribe to Mountain room
      ws.send(JSON.stringify({ type: 'subscribe_mountain' }));
      ws.addEventListener('message', handleWsMessage);

      return () => {
        ws.send(JSON.stringify({ type: 'unsubscribe_mountain' }));
        ws.removeEventListener('message', handleWsMessage);
      };
    } else {
      // Fallback to polling
      fetchGameState();
      const interval = setInterval(fetchGameState, 300); // Poll every 300ms
      return () => clearInterval(interval);
    }
  }, [isOpen, API_URL, handcashUser?.handle, wsRef]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Sound effects based on game events
  useEffect(() => {
    if (!gameState?.round) return;

    const round = gameState.round;
    const phase = round.phase;
    const myHandle = handcashUser?.handle;
    const me = myHandle ? (round.climbers?.[myHandle] || round.climbers?.['$' + myHandle]) : null;

    // Phase change sounds
    if (phase !== lastPhase.current) {
      if (phase === 'betting' && lastPhase.current === 'crashed') {
        // New round starting
        Sounds?.roundStart?.();
      } else if (phase === 'climbing' && lastPhase.current === 'betting') {
        // Climbing begins!
        Sounds?.climbStart?.();
      } else if (phase === 'crashed') {
        // Avalanche!
        Sounds?.avalanche?.();
        // If I was climbing and didn't cash out, I fell
        if (wasInRound.current && me && !me.cashedOut) {
          setTimeout(() => Sounds?.fell?.(), 400);
        }
      }
      lastPhase.current = phase;
    }

    // Climbing tick sound (only when actively climbing and in round)
    if (phase === 'climbing' && round.currentLevel > lastLevel.current) {
      // Play tick every 0.5x increase
      const levelDiff = round.currentLevel - lastLevel.current;
      if (levelDiff >= 0.1 && me && !me.cashedOut) {
        Sounds?.climbTick?.();
      }
      lastLevel.current = round.currentLevel;
    }

    // Got attacked detection (slip count increased)
    if (me && me.slipped > lastSlipCount.current) {
      Sounds?.gotHit?.();
      lastSlipCount.current = me.slipped;
    }

    // Track if I'm in the round
    wasInRound.current = !!me;

    // Reset level tracking on new round
    if (round.id !== lastRoundId.current) {
      lastLevel.current = 1;
      lastSlipCount.current = 0;
      lastBettingSeconds.current = null;
    }

    // Countdown beeps in last 5 seconds of betting
    if (phase === 'betting' && round.bettingSecondsLeft !== undefined) {
      const secs = round.bettingSecondsLeft;
      if (secs <= 5 && secs > 0 && secs !== lastBettingSeconds.current) {
        Sounds?.countdown?.();
      }
      lastBettingSeconds.current = secs;
    }
  }, [gameState, handcashUser?.handle, Sounds]);

  // Join the round
  const joinRound = async () => {
    if (!sessionId || isJoining) return;
    setIsJoining(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/mountain/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          amount: betAmount,
          tier: selectedTier,
          character: selectedCharacter,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setGameWallet(data.newBalance);
        Sounds?.bet?.();
        if (triggerRakeNotification) {
          // Convert tier fee (in cents) to sats: cents / 100 = USD, then USD / bsvPrice * 100M = sats
          const feeCents = TIERS[selectedTier].fee;
          const feeSats = Math.floor((feeCents / 100 / (bsvPrice || 18.5)) * 100000000);
          triggerRakeNotification(feeSats, 'mountain');
        }
      } else {
        setError(data.error || 'Failed to join');
        Sounds?.error?.();
      }
    } catch (e) {
      setError(e.message);
      Sounds?.error?.();
    }

    setIsJoining(false);
  };

  // Cash out
  const cashOut = async () => {
    if (!sessionId || isCashingOut || !myClimber) return;
    setIsCashingOut(true);

    try {
      const res = await fetch(`${API_URL}/api/mountain/cashout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      const data = await res.json();

      if (data.success) {
        setGameWallet(data.newBalance);
        Sounds?.flagPlant?.(); // Plant that flag!
      } else {
        setError(data.error || 'Cashout failed');
        Sounds?.error?.();
      }
    } catch (e) {
      setError(e.message);
      Sounds?.error?.();
    }

    setIsCashingOut(false);
  };

  // Attack another climber
  const attackClimber = async (targetHandle) => {
    if (!sessionId || isAttacking) return;
    setIsAttacking(true);

    try {
      const res = await fetch(`${API_URL}/api/mountain/attack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, targetHandle }),
      });

      const data = await res.json();

      if (data.success) {
        Sounds?.attack?.(); // Attack whoosh!
      } else {
        setError(data.error);
        Sounds?.error?.();
      }
    } catch (e) {
      setError(e.message);
    }

    setIsAttacking(false);
  };

  // Send chat message
  const sendChat = async () => {
    if (!chatInput.trim() || !sessionId) return;

    try {
      await fetch(`${API_URL}/api/mountain/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, text: chatInput.trim() }),
      });
      setChatInput('');
    } catch (e) {
      console.error('Chat error:', e);
    }
  };

  if (!isOpen) return null;

  const round = gameState?.round;
  const phase = round?.phase || 'betting';
  const currentLevel = round?.currentLevel || 1.00;
  const climbers = round?.climbers || {};
  const climberCount = Object.keys(climbers).length;
  const bettingSecondsLeft = round?.bettingSecondsLeft;
  const attacksRemaining = myClimber ? (myClimber.attacks - myClimber.attacksUsed) : 0;

  // Get other climbers for attack targets
  const myHandle = handcashUser?.handle;
  const myHandleNoPrefix = myHandle?.replace(/^\$/, '');
  const attackTargets = Object.entries(climbers)
    .filter(([h, c]) => h !== myHandle && h !== myHandleNoPrefix && !c.cashedOut)
    .map(([h, c]) => ({ handle: h, ...c }));

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '12px',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: COLORS.bg,
          border: `2px solid ${COLORS.cyan}`,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '950px',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gap: '0',
        }}
      >
        {/* LEFT SIDE - Mountain & Controls */}
        <div style={{ padding: '20px', borderRight: `1px solid ${COLORS.mountain}` }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <div>
              <h2 style={{ color: COLORS.textBright, margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>🏔️</span> THE LAST DEGEN
              </h2>
              <div style={{ color: COLORS.text, fontSize: '11px' }}>
                {climberCount}/{gameState?.config?.maxClimbers || 15} climbers | Cash out before avalanche!
              </div>
              <div style={{ color: COLORS.text, fontSize: '10px', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <span title="Pick Bronze (1 atk), Silver (2 atks), or Gold (3 atks)">1. Pick tier</span>
                <span title="Bet 10K-250K PaperBucks">2. Place bet</span>
                <span title="Watch the multiplier climb from 1x to 5x max">3. Climb!</span>
                <span title="Cash out anytime to lock in your multiplier">4. Cash out = safe</span>
                <span title="Use attacks to make other climbers slip back">5. Attack rivals!</span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.text,
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              x
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              backgroundColor: COLORS.danger + '33',
              border: `1px solid ${COLORS.danger}`,
              borderRadius: '8px',
              padding: '8px 12px',
              marginBottom: '12px',
              color: COLORS.danger,
              fontSize: '12px',
            }}>
              {error}
            </div>
          )}

          {/* Mountain View */}
          <Mountain
            climbers={climbers}
            currentLevel={currentLevel}
            myHandle={myHandle}
            phase={phase}
          />

          {/* Controls */}
          <div style={{ marginTop: '16px' }}>
            {/* Pre-game: Character, Tier & Bet Selection */}
            {!myClimber && phase === 'betting' && (
              <>
                {/* Character Selection */}
                <div style={{ color: COLORS.gold, fontSize: '11px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>SELECT CHARACTER</span>
                  {bettingSecondsLeft !== null && (
                    <span style={{ color: bettingSecondsLeft <= 5 ? COLORS.danger : COLORS.safe }}>
                      {bettingSecondsLeft}s left
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '6px',
                  marginBottom: '12px',
                  maxHeight: '140px',
                  overflowY: 'auto',
                }}>
                  {Object.entries(CLIMBERS).map(([id, char]) => {
                    const takenCharacters = Object.values(climbers).map(c => c.character);
                    const isTaken = takenCharacters.includes(id);
                    const isSelected = selectedCharacter === id;

                    return (
                      <button
                        key={id}
                        onClick={() => !isTaken && setSelectedCharacter(id)}
                        disabled={isTaken}
                        style={{
                          padding: '6px 4px',
                          backgroundColor: isSelected ? '#1a2233' : isTaken ? '#111' : COLORS.bg,
                          border: isSelected ? `2px solid ${COLORS.cyan}` : isTaken ? '1px solid #333' : `1px solid ${COLORS.mountain}`,
                          borderRadius: '6px',
                          cursor: isTaken ? 'not-allowed' : 'pointer',
                          textAlign: 'center',
                          opacity: isTaken ? 0.4 : 1,
                        }}
                      >
                        <div style={{ transform: 'scale(0.8)', display: 'flex', justifyContent: 'center' }}>
                          <ClimberSprite character={id} size={18} isMe={false} isSafe={false} />
                        </div>
                        <div style={{
                          color: isSelected ? COLORS.cyan : isTaken ? '#666' : COLORS.text,
                          fontSize: '8px',
                          fontWeight: 'bold',
                          marginTop: '2px',
                        }}>
                          {char.name}
                          {isTaken && ' ❌'}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Tier Selection */}
                <div style={{ color: COLORS.gold, fontSize: '11px', marginBottom: '8px' }}>
                  SELECT TIER
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {Object.entries(TIERS).map(([id, tier]) => (
                    <button
                      key={id}
                      onClick={() => setSelectedTier(id)}
                      style={{
                        flex: 1,
                        padding: '12px 8px',
                        backgroundColor: selectedTier === id ? '#1a2233' : COLORS.bg,
                        border: selectedTier === id ? `2px solid ${tier.color}` : `1px solid ${COLORS.mountain}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '16px' }}>{tier.emoji}</div>
                      <div style={{
                        color: selectedTier === id ? tier.color : COLORS.text,
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                      }}>
                        {id}
                      </div>
                      <div style={{ color: COLORS.text, fontSize: '9px' }}>
                        {tier.fee}c / {tier.attacks} atk
                      </div>
                    </button>
                  ))}
                </div>

                {/* Bet Amount */}
                <div style={{ color: COLORS.gold, fontSize: '11px', marginBottom: '8px' }}>
                  BET AMOUNT
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                  {[10000, 25000, 50000, 100000, 250000].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setBetAmount(amt)}
                      style={{
                        flex: 1,
                        padding: '10px 6px',
                        backgroundColor: betAmount === amt ? '#1a2233' : COLORS.bg,
                        border: betAmount === amt ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.mountain}`,
                        borderRadius: '8px',
                        color: betAmount === amt ? COLORS.gold : COLORS.text,
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      {amt >= 1000000 ? (amt / 1000000) + 'M' : (amt / 1000) + 'K'}
                    </button>
                  ))}
                </div>

                {/* Join Button */}
                <button
                  onClick={joinRound}
                  disabled={isJoining || gameWallet < betAmount}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: gameWallet < betAmount
                      ? '#333'
                      : `linear-gradient(180deg, ${COLORS.cyan} 0%, #2a9d8f 100%)`,
                    border: 'none',
                    borderRadius: '10px',
                    color: gameWallet < betAmount ? '#666' : '#000',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: gameWallet < betAmount ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isJoining ? 'JOINING...' : `START CLIMB (${(betAmount / 1000)}K)`}
                </button>

                <div style={{
                  color: COLORS.text,
                  fontSize: '10px',
                  textAlign: 'center',
                  marginTop: '8px',
                }}>
                  Balance: {(gameWallet / 1000).toFixed(0)}K | {TIERS[selectedTier].fee}c fee
                </div>
              </>
            )}

            {/* In-Game: Cash Out Button */}
            {myClimber && !myClimber.cashedOut && phase === 'climbing' && (
              <button
                onClick={cashOut}
                disabled={isCashingOut}
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: COLORS.safe,
                  border: 'none',
                  borderRadius: '10px',
                  color: '#000',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  animation: 'pulse 0.5s infinite',
                }}
              >
                {isCashingOut ? '...' : `PLANT FLAG @ ${currentLevel.toFixed(2)}x`}
              </button>
            )}

            {/* Cashed out message */}
            {myClimber?.cashedOut && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                backgroundColor: COLORS.safe + '22',
                border: `1px solid ${COLORS.safe}`,
                borderRadius: '10px',
                color: COLORS.safe,
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⛳</div>
                <div style={{ fontWeight: 'bold' }}>SAFE @ {myClimber.cashoutLevel?.toFixed(2)}x</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Won {((myClimber.payout || 0) / 1000).toFixed(0)}K PB
                </div>
              </div>
            )}

            {/* Waiting for next round */}
            {!myClimber && phase !== 'betting' && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                color: COLORS.text,
              }}>
                {phase === 'climbing' && climberCount > 0
                  ? 'Round in progress - watch the action above!'
                  : 'Next round starting soon... Watch the demo!'}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - Chat, Attacks, Leaderboard */}
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Live Climbers */}
          <div style={{
            backgroundColor: COLORS.chat,
            borderRadius: '8px',
            padding: '12px',
            maxHeight: '120px',
            overflow: 'auto',
          }}>
            <div style={{ color: COLORS.gold, fontSize: '10px', marginBottom: '8px' }}>
              CLIMBERS ({climberCount})
            </div>
            {Object.entries(climbers).map(([h, c]) => (
              <div key={h} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '3px 0',
                borderBottom: `1px solid ${COLORS.mountain}`,
                fontSize: '11px',
              }}>
                <span style={{ color: c.cashedOut ? COLORS.safe : COLORS.textBright }}>
                  {TIERS[c.tier]?.emoji} {h}
                </span>
                <span style={{ color: c.cashedOut ? COLORS.safe : COLORS.text }}>
                  {c.cashedOut ? `${c.cashoutLevel?.toFixed(2)}x ⛳` : `${c.level?.toFixed(2)}x`}
                </span>
              </div>
            ))}
            {climberCount === 0 && (
              <div style={{ color: COLORS.text, fontSize: '10px', fontStyle: 'italic' }}>
                No climbers yet...
              </div>
            )}
          </div>

          {/* Attacks Panel */}
          {myClimber && !myClimber.cashedOut && phase === 'climbing' && attacksRemaining > 0 && (
            <div style={{
              backgroundColor: COLORS.chat,
              borderRadius: '8px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.danger, fontSize: '10px', marginBottom: '8px' }}>
                ATTACKS ({attacksRemaining} left)
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {attackTargets.map(t => (
                  <button
                    key={t.handle}
                    onClick={() => attackClimber(t.handle)}
                    disabled={isAttacking}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: COLORS.danger + '33',
                      border: `1px solid ${COLORS.danger}`,
                      borderRadius: '6px',
                      color: COLORS.danger,
                      fontSize: '10px',
                      cursor: isAttacking ? 'wait' : 'pointer',
                    }}
                  >
                    SLIP {t.handle}
                  </button>
                ))}
                {attackTargets.length === 0 && (
                  <div style={{ color: COLORS.text, fontSize: '10px' }}>No targets available</div>
                )}
              </div>
            </div>
          )}

          {/* Chat */}
          <div style={{
            backgroundColor: COLORS.chat,
            borderRadius: '8px',
            padding: '12px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '150px',
          }}>
            <div style={{ color: COLORS.gold, fontSize: '10px', marginBottom: '8px' }}>
              LIVE CHAT (FREE)
            </div>
            <div
              ref={chatRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '8px',
              }}
            >
              {chatMessages.length === 0 ? (
                <div style={{ color: COLORS.text, fontSize: '10px', fontStyle: 'italic' }}>
                  No messages yet...
                </div>
              ) : (
                chatMessages.map((msg, i) => (
                  <div key={msg.id || i} style={{
                    fontSize: '11px',
                    marginBottom: '4px',
                    color: msg.isSystem ? COLORS.cyan : COLORS.textBright,
                    fontStyle: msg.isSystem ? 'italic' : 'normal',
                  }}>
                    {msg.isSystem ? (
                      msg.text
                    ) : (
                      <>
                        <span style={{ color: COLORS.gold }}>{msg.handle}:</span> {msg.text}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && sendChat()}
                placeholder="Type..."
                maxLength={100}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: COLORS.bg,
                  border: `1px solid ${COLORS.mountain}`,
                  borderRadius: '6px',
                  color: COLORS.textBright,
                  fontSize: '11px',
                  outline: 'none',
                }}
              />
              <button
                onClick={sendChat}
                style={{
                  padding: '8px 12px',
                  backgroundColor: COLORS.gold,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                Send
              </button>
            </div>
          </div>

          {/* Leaderboard */}
          <div style={{
            backgroundColor: COLORS.chat,
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ color: COLORS.gold, fontSize: '10px', marginBottom: '8px' }}>
              TOP CLIMBERS
            </div>
            {gameState?.leaderboard?.slice(0, 5).map((entry, i) => (
              <div key={entry.handle} style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '3px 0',
                fontSize: '10px',
                color: COLORS.textBright,
              }}>
                <span>{i + 1}. {entry.handle}</span>
                <span style={{ color: entry.points >= 0 ? COLORS.safe : COLORS.danger }}>
                  {entry.points >= 0 ? '+' : ''}{(entry.points / 1000).toFixed(0)}K
                </span>
              </div>
            ))}
            {(!gameState?.leaderboard || gameState.leaderboard.length === 0) && (
              <div style={{ color: COLORS.text, fontSize: '10px', fontStyle: 'italic' }}>
                No leaders yet...
              </div>
            )}
          </div>

          {/* History */}
          <div style={{
            backgroundColor: COLORS.chat,
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ color: COLORS.text, fontSize: '10px', marginBottom: '8px' }}>
              RECENT AVALANCHES
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {gameState?.history?.slice(0, 8).map((h, i) => (
                <span key={h.roundId || i} style={{
                  padding: '4px 8px',
                  backgroundColor: h.crashLevel >= 3 ? COLORS.safe + '33' : h.crashLevel >= 2 ? COLORS.gold + '33' : COLORS.danger + '33',
                  border: `1px solid ${h.crashLevel >= 3 ? COLORS.safe : h.crashLevel >= 2 ? COLORS.gold : COLORS.danger}`,
                  borderRadius: '4px',
                  color: h.crashLevel >= 3 ? COLORS.safe : h.crashLevel >= 2 ? COLORS.gold : COLORS.danger,
                  fontSize: '10px',
                }}>
                  {h.crashLevel?.toFixed(2)}x
                </span>
              ))}
              {(!gameState?.history || gameState.history.length === 0) && (
                <span style={{ color: COLORS.text, fontSize: '10px' }}>No history</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
};

export default MountainClimber;
